/**
 * Where a person's own file lives.
 *
 * Decision 0017. A `media` value holds a REFERENCE and never the content, for
 * one measured reason: `projection` copies a value on every read, so a `data:`
 * URI would put a 200MB video inside a property that every lens over that
 * object pays for whether it draws it or not.
 *
 * ## The reference is a scheme, and it is defined here once
 *
 * `morph-blob:<id>`. The renderer refuses any media URL that is not `http(s)`
 * — `packages/runtime/test/media.test.ts` pins six schemes against exactly that
 * — and decision 0017 keeps that guard untouched: the HOST resolves a reference
 * to an object URL it minted from bytes it just read out of its own store, so
 * what reaches the renderer is a URL the page created rather than a string that
 * arrived in data. `blobRef` and `blobIdIn` are the only two functions that know
 * the spelling, because a scheme parsed in two places is a scheme two layers
 * disagree about.
 *
 * ## Content-addressed, and what that does and does not buy
 *
 * The id is the SHA-256 of the bytes. Two objects given the same photograph
 * store it once and the second `put` is a no-op — which matters here because a
 * pool is a set of objects that share a shape, and the same file arriving twice
 * is the ordinary case rather than the corner.
 *
 * It does NOT buy eviction. Nothing here deletes bytes when the last reference
 * to them goes away, and decision 0017 names that as undecided rather than
 * quietly solved: with content addressing, "the last reference" is a question
 * about every object in the store, and answering it wrongly deletes a file
 * somebody still has open.
 */

import {
  derivativeIndexOver, memoryDerivativeRows, type DerivativeIndex,
} from './derivatives.js';

/** The digest of the bytes, lowercase hex. Branded so a caller cannot pass a
 *  property key where a blob id belongs — the two are both strings and the
 *  compiler is the only thing that will ever notice.
 *
 *  The brand is declared here rather than imported, the same way `ids.ts`
 *  declares its own: `brand.ts` holds the three unforgeable write brands and is
 *  deliberately not re-exported, so reaching into it for a type alias would
 *  make a file whose whole point is being unreachable slightly reachable. */
export type BlobId = string & { readonly __brand: 'BlobId' };

/** What the store knows about a blob without opening it. `bytes` is the LENGTH:
 *  a size that costs nothing to read is the difference between a size ceiling
 *  that can be enforced and one that is a comment. */
/**
 * How a caller names the blob it wants.
 *
 * A typed identity is what a `morph-blob:<digest>;<mime>` reference parses to
 * and is the form every new write emits. A bare `BlobId` is the legacy shape,
 * kept resolvable by decision `0047` — and kept deliberately distinguishable
 * from the typed one, so a caller cannot pass a digest where a type was
 * required and get a row chosen for it.
 */
export type BlobLookup = BlobId | { readonly id: BlobId; readonly mime: string };

export interface BlobRef {
  readonly id: BlobId;
  readonly mime: string;
  readonly bytes: number;
}

export interface StoredBlob {
  /**
   * Backed by an `ArrayBuffer`, not merely by `ArrayBufferLike`.
   *
   * `Uint8Array` is generic over its buffer, so the bare type admits one backed
   * by a `SharedArrayBuffer` — and several browser APIs a caller will
   * immediately reach for refuse that, `new Blob([bytes])` and
   * `crypto.subtle.digest` among them. Every backend here produces an
   * `ArrayBuffer`-backed array anyway: the memory one copies into a fresh
   * `Uint8Array`, and IndexedDB structured-clones into one. Saying so is the
   * difference between a caller writing `new Blob([stored.bytes])` and a caller
   * writing a defensive copy of a file that may be 200MB.
   */
  readonly bytes: Uint8Array<ArrayBuffer>;
  readonly mime: string;
}

/**
 * Bytes, keyed by their own digest.
 *
 * A sibling of `StorePersistence` rather than part of it, because blobs have a
 * different lifecycle from the object graph: large, immutable, and never loaded
 * wholesale. `load()` reads every object, key, edge and manifest on open; doing
 * that to blobs would pull every photograph in the store into memory to show a
 * list of titles.
 */
export interface BlobStore {
  /** Store bytes and return what to reference them by. Idempotent: the same
   *  bytes yield the same id and the second call writes nothing.
   *
   *  The caller keeps its buffer. A backend that moves the write off the main
   *  thread must therefore copy, and `putOwned` is how a caller says it does
   *  not need to. */
  put(bytes: Uint8Array, mime: string): Promise<BlobRef>;
  /**
   * `put`, for a caller willing to LEND its buffer.
   *
   * Decision `0043` moves source hashing and the IndexedDB write behind a
   * Worker and says the bytes *"should cross into the Worker by ownership
   * transfer where the platform permits it, rather than by a second
   * main-thread copy"*. A transfer detaches the caller's array — measured, not
   * assumed: `bytes.byteLength` is `0` on the sending side immediately after
   * `postMessage(…, [buffer])`.
   *
   * So the bytes come BACK. `ingest.ts` needs them after the store returns —
   * `onStored` mints an object URL from them and `derive` decodes them — and a
   * signature that only returns a `BlobRef` cannot hand them over. This one
   * does, and the caller must use the returned array rather than the one it
   * passed in.
   *
   * Optional, and `put` stays the compatibility seam `0043` names. A backend
   * with nothing to transfer to implements this by calling `put` and handing
   * the same array straight back, which is correct and free.
   *
   * The alternative was keeping one method and copying defensively. Measured on
   * a 64 MiB source, that copy is ~40ms locally — small against a 1,824ms
   * budget, and roughly 200ms on the deployed machine, which is still inside
   * it. So this is a preference the decision states rather than a necessity,
   * and the number is recorded because it is also the fallback if a caller
   * cannot give up its buffer.
   */
  putOwned?(
    bytes: Uint8Array<ArrayBuffer>, mime: string,
  ): Promise<{ readonly ref: BlobRef; readonly bytes: Uint8Array<ArrayBuffer> }>;
  /**
   * Resolve a reference — by its TYPED identity, or by a legacy digest.
   *
   * Decision `0047`: *"APIs that resolve a person's reference (`get` and
   * `stat`) must take the typed reference identity, not silently discard its
   * media type and look up by digest alone."* A `{ id, mime }` finds the exact
   * accepted pair or nothing; a bare `BlobId` is the legacy form the same
   * decision keeps resolving during migration.
   *
   * Both, rather than only the typed form, because `0047` is explicit that
   * legacy references stay resolvable — and a signature that could not express
   * one would force every caller holding an old value to invent a type, which
   * is precisely the forgery the typed row exists to refuse.
   */
  get(at: BlobLookup): Promise<StoredBlob | undefined>;
  /** Present without reading the bytes — for a size check, or for deciding
   *  whether a reference still resolves before minting a URL for it. */
  stat(at: BlobLookup): Promise<BlobRef | undefined>;
  delete(id: BlobId): Promise<void>;
  /** Every blob held. The floor under any future eviction, and the only way to
   *  answer "how much is this store carrying" without a second bookkeeping
   *  record that can drift from the truth. */
  list(): Promise<readonly BlobRef[]>;
  /**
   * Which transform produced which blob. Decision 0041 puts this *"at the
   * blob-store boundary"* rather than in the demo or on an object, for the
   * reason the rest of this file already argues: the id of a derivative is the
   * digest of its own bytes, so nothing in it says what it was derived FROM.
   * Without the index, deciding whether a thumbnail already exists costs a
   * decode of the source, which is the entire expense the thumbnail avoids.
   */
  readonly derivatives: DerivativeIndex;
}

/* ──────────────────────────────────────────────────────────── the scheme */

const SCHEME = 'morph-blob:';

/**
 * The canonical SHAPE of a media type in a reference — `type/subtype`, no
 * parameters, lowercase.
 *
 * Decision `0047` requires the scheme to *"round-trip every accepted media type
 * canonically and reject duplicate, malformed, or unrecognised type
 * selectors."* This validates the shape and deliberately not the membership,
 * because the accepted SET is not the store's to know: `apps/demo`'s `ACCEPTED`
 * list is the door's, and derivative encoders pick their own. What decides
 * whether a pair was accepted is the store's metadata row, which is `0047`'s
 * whole point — *"the type in a reference is a selector, not an unchecked
 * assertion"*, and a selector naming no row is an unresolved reference rather
 * than a reinterpretation.
 *
 * So: a shape check here, an existence check at resolution. Putting the closed
 * list here instead would move an application vocabulary into the store and
 * still not answer the question resolution has to ask.
 */
const MIME = /^[a-z]+\/[a-z0-9][a-z0-9.+-]*$/;

/**
 * The reference a `media` value carries — digest AND accepted media type.
 *
 * Decision `0047`: *"a `morph-blob:` reference names both the digest and the
 * media type under which those bytes were accepted"*, so identical bytes
 * accepted as `audio/mp4` and later as `video/mp4` produce two stable
 * references over one byte record instead of one reference whose meaning
 * depends on ingest order.
 *
 * Takes the whole ref rather than an id, so a caller cannot mint an untyped
 * one by accident — *"new writes must always emit typed references"*, and the
 * type is always sitting beside the id at every call site that had one.
 */
export const blobRef = (ref: { readonly id: BlobId; readonly mime: string }): string => {
  acceptedMime(ref.mime);
  return `${SCHEME}${ref.id};${ref.mime}`;
};

/**
 * The check `put` makes BEFORE hashing or writing anything.
 *
 * @Codex, P2 on `be5b9ec`: *"When a caller uses the public `BlobStore.put()`
 * boundary with a noncanonical MIME such as `IMAGE/PNG` or `image/png;
 * charset=utf-8`, this implementation — and the IndexedDB implementation —
 * stores and returns that value without validation. The returned `BlobRef` can
 * be resolved and listed, but `blobRef(ref)` then throws because the
 * typed-reference scheme rejects the MIME, leaving a supposedly accepted row
 * that cannot be represented by any new reference; on IndexedDB the unusable
 * row and bytes are already durable."*
 *
 * Exactly the gap `0047` leaves if the two checks sit at different doors: the
 * decision says a pair *"is published only through the blob store's accepted
 * `put`/`putOwned` boundary"*, so a pair the scheme cannot name must not be
 * publishable there. A row nothing can reference is not an acceptance.
 *
 * One function rather than two copies of the regex, and it is called at the
 * TOP of `put` rather than beside the row write: on IndexedDB the bytes are
 * durable by the time the row is built, so a refusal any later leaves exactly
 * the orphan the finding describes.
 */
/** Whether a media type round-trips through the reference scheme. The
 *  predicate half of `acceptedMime`, exported because the v5 migration has to
 *  SKIP a malformed historical row rather than throw on one — a vault full of
 *  them would otherwise be a vault that will not open. One definition, because
 *  two spellings of "canonical" is how a row the scheme cannot encode gets
 *  published as one it can. */
export function isAcceptedMime(mime: string): boolean {
  return MIME.test(mime);
}

export function acceptedMime(mime: string): void {
  if (isAcceptedMime(mime)) return;
  throw new Error(
    `"${mime}" is not a canonical media type, so a reference to it could `
    + 'not be parsed back — 0047 requires the scheme to round-trip every '
    + 'accepted type canonically',
  );
}

/**
 * The id inside a reference, or `undefined` for anything else.
 *
 * Anything else includes an ordinary `https://` URL, which is not an error: the
 * demo's seeded media is external and stays that way, so a host resolving
 * references walks past those untouched.
 *
 * Validates the SHAPE of the id rather than only the prefix. `morph-blob:` plus
 * arbitrary text would otherwise be a way to get an attacker-chosen string into
 * whatever the host does with the id next, and "the host looks it up in its own
 * store" is a property of today's host rather than a guarantee.
 */
export function blobIdIn(reference: unknown): BlobId | undefined {
  return blobRefIn(reference)?.id;
}

/**
 * The digest AND the media type a reference selects, or `undefined`.
 *
 * `mime` is absent for a LEGACY digest-only reference, which decision `0047`
 * keeps resolvable: *"Legacy digest-only references resolve through their
 * existing stored type during migration."* Absent is not the same as wrong, and
 * a caller must not treat it as a type it can choose — the same decision is
 * explicit that legacy fallback is *"compatibility, not permission to mint
 * another untyped reference."*
 *
 * Rejections, each one named by `0047`:
 *
 * - **duplicate** — more than one selector (`…;a/b;c/d`). Taking the first or
 *   the last would make two references that differ in text resolve the same,
 *   which is the ambiguity the typed scheme exists to remove.
 * - **malformed** — a selector that is not `type/subtype`, empty, uppercase, or
 *   carrying parameters.
 * - **unrecognised** — the same check: anything outside the canonical shape.
 *
 * The digest check is unchanged and still validates the SHAPE rather than the
 * prefix, for the reason recorded above it.
 */
export function blobRefIn(
  reference: unknown,
): { readonly id: BlobId; readonly mime?: string } | undefined {
  if (typeof reference !== 'string' || !reference.startsWith(SCHEME)) return undefined;
  const body = reference.slice(SCHEME.length);
  const parts = body.split(';');
  // Zero separators is legacy; one is typed; more is a duplicate selector and
  // is refused rather than resolved by picking one.
  if (parts.length > 2) return undefined;
  const [id, mime] = parts;
  if (!/^[0-9a-f]{64}$/.test(id ?? '')) return undefined;
  if (mime === undefined) return { id: id as BlobId };
  return MIME.test(mime) ? { id: id as BlobId, mime } : undefined;
}

/* ─────────────────────────────────────────────────────── content address */

/**
 * SHA-256 of the bytes, as lowercase hex.
 *
 * `crypto.subtle` because it is the one digest available in both places this
 * runs — a browser and Node — without a dependency. It is async everywhere,
 * which is why `put` is, and that is the honest shape: hashing 200MB is not
 * something to pretend is free.
 */
export async function digestOf(bytes: Uint8Array): Promise<BlobId> {
  /**
   * Timed, because the caller cannot time it and the difference decides a fix.
   *
   * Task `0119` clause 3 asks for the stall attributed to *"hash, put, derive,
   * write, repaint"*, and `ingest.ts` can only wrap `blobs.put()`, which is
   * hash AND clone in one call. Hashing separately in the caller to time it
   * would hash a 64 MiB file twice and add roughly 800ms of the very blocking
   * work the budget measures — so the measure has to be emitted where the work
   * happens, which is here.
   *
   * The name is `morph.store.digest` rather than `morph.ingest.hash`: the store
   * has no idea it is inside an ingest, and a measure that claims otherwise is
   * wrong the first time something else calls this. `e2e/ingest-budget.mjs`
   * folds it into the phase table and subtracts it from `store-source` to get
   * the clone, which is what task `0125` asks for before anyone builds a Worker.
   */
  // `typeof performance === 'object'` rather than `typeof performance?.now`.
  // The optional-chaining form reads as the safe one and is not: `typeof`
  // suppresses a ReferenceError only for a BARE identifier, so a member
  // expression evaluates `performance` and throws first if it is undeclared.
  // `apps/demo/src/ingest.ts` already had this right.
  const at = typeof performance === 'object'
    && typeof performance.now === 'function' ? performance.now() : undefined;
  try {
    return await sha256(bytes);
  } finally {
    if (at !== undefined && typeof performance.measure === 'function') {
      // Guarded: `measure` throws on a name clash in some engines, and a
      // diagnostic that can fail a store write is worse than no diagnostic.
      // Bounded too — an entry lives until something clears it, and a store
      // that runs for a session would accumulate one per file forever.
      try {
        if (performance.getEntriesByName('morph.store.digest', 'measure').length > 32) {
          performance.clearMeasures('morph.store.digest');
        }
        performance.measure('morph.store.digest', { start: at });
      } catch { /* no-op */ }
    }
  }
}

async function sha256(bytes: Uint8Array): Promise<BlobId> {
  // Copied into a fresh ArrayBuffer, and the reason is not the one I first
  // wrote here. That comment claimed a view onto a larger buffer — a subarray,
  // or a view into a pooled Node Buffer — would hand `digest` the WHOLE buffer
  // and hash bytes the caller never passed. **That is false.** `digest` takes a
  // `BufferSource` and honours `byteOffset` and `byteLength`, checked against
  // Node's WebCrypto: a subarray of 'XXXXhelloYYYY' and a standalone 'hello'
  // produce the same digest. Removing the copy on that basis passed all
  // thirteen tests.
  //
  // It failed the TYPECHECK, which is the real reason. `Uint8Array` is generic
  // over `ArrayBufferLike`, so a value of that type may be backed by a
  // `SharedArrayBuffer`, and `digest` accepts only an `ArrayBuffer`-backed one.
  // The copy is how you produce that without a cast — and a cast here would be
  // asserting the very thing the compiler is saying it cannot see.
  const owned = new Uint8Array(bytes.byteLength);
  owned.set(bytes);
  const hash = await crypto.subtle.digest('SHA-256', owned);
  return [...new Uint8Array(hash)]
    .map((b) => b.toString(16).padStart(2, '0')).join('') as BlobId;
}

/* ────────────────────────────────────────────────────────────── in memory */

/** Nothing survives the tab, which is the same bargain `nullPersistence` makes
 *  and for the same reason: the tests run against this, so it has to be the
 *  real interface rather than a second implementation that can disagree. */
export function createMemoryBlobs(): BlobStore {
  /**
   * TWO records, per decision `0047`: *"one immutable byte record keyed by
   * `BlobId`, the digest; and one accepted-reference metadata row keyed by
   * `(BlobId, mime)`."*
   *
   * The rows do not copy the bytes — they carry the length and the canonical
   * type — so accepting the same file under a second media type costs a row
   * rather than another copy of a 64 MiB source. That is the whole reason the
   * split is worth having rather than keying the byte map on the pair.
   */
  const bytes = new Map<BlobId, Uint8Array<ArrayBuffer>>();
  const rows = new Map<string, BlobRef>();
  const rowKey = (id: BlobId, mime: string) => `${id};${mime}`;

  /**
   * The row a lookup selects.
   *
   * A typed identity finds the EXACT pair or nothing — `0047`: *"Forging
   * another type onto a digest, including another otherwise accepted Morph
   * media type, does not reinterpret the bytes; it is an unresolved
   * reference."*
   *
   * A bare `BlobId` is a LEGACY reference, and resolves through whichever row
   * the digest has. The same decision keeps those working during migration.
   * There is only ever one for a digest that was accepted once, so this is
   * ambiguous exactly when a second type has been accepted since the legacy
   * reference was written — and then the first row is the one that reference
   * meant, which is why insertion order decides rather than a scan.
   */
  /**
   * A COPY of a stored row, never the row.
   *
   * @Codex, P2 on `8e4315a`: *"When a JavaScript caller mutates a `BlobRef`
   * returned by `put()`, `stat()`, or `list()`, the memory backend's stored
   * metadata changes because the same object is retained in `rows` and handed
   * back here; `readonly` is not a runtime guarantee. Changing the returned
   * `mime`, for example, makes later `get()` calls report that forged type
   * while the row remains indexed under the original pair."*
   *
   * A second backend disagreement, and the same shape as the one above:
   * IndexedDB structured-clones in both directions, so it was already safe and
   * this one was not. `0047` makes an accepted reference an identity the store
   * authorises — an identity a caller can edit in place is not authorised by
   * anything.
   *
   * This is the rule the bytes already followed. `put` copies on the way in and
   * `get` copies on the way out, both with the comment *"a caller that writes
   * into what it got back would silently change what every other reference to
   * that id resolves to"*. That sentence was true of the metadata too and only
   * the bytes were treated as if it were.
   */
  const detach = (r: BlobRef): BlobRef => ({ id: r.id, mime: r.mime, bytes: r.bytes });

  const rowFor = (at: BlobId | { readonly id: BlobId; readonly mime: string }) => {
    if (typeof at !== 'string') return rows.get(rowKey(at.id, at.mime));
    for (const r of rows.values()) if (r.id === at) return r;
    return undefined;
  };

  return {
    // Built over the same `derivativeIndexOver` the IndexedDB backend uses, so
    // the staleness and conflict rules are one implementation rather than two
    // that can disagree. Presence is `held.has`, which is `stat` without the
    // allocation.
    derivatives: derivativeIndexOver(
      // The same question the index asks, in the SYNCHRONOUS form the rows need:
      // their atomicity is "nothing yields between the comparison and the
      // write", and an async probe is a yield. See `putIf`.
      // The BYTE record, by digest. 0047 keeps the derivative index
      // identifying source bytes by digest — a second accepted type does not
      // rewrite an existing mapping — so this asks about bytes, not rows.
      memoryDerivativeRows((id) => bytes.has(id)),
      async (id) => (bytes.has(id) ? id : undefined),
    ),
    async put(source, mime) {
      // BEFORE the hash. A type the scheme cannot name must not become a row.
      acceptedMime(mime);
      const id = await digestOf(source);
      // Copied on the way in. A caller that reuses its buffer — which is what
      // a streaming read does — would otherwise mutate what the store holds
      // after the id was computed, and the digest would stop describing the
      // content it names.
      if (!bytes.has(id)) {
        const owned = new Uint8Array(source.byteLength);
        owned.set(source);
        bytes.set(id, owned);
      }
      /**
       * `0047`: *"Repeating `put(bytes, sameMime)` remains idempotent.
       * Repeating the same bytes with a different accepted media type publishes
       * another metadata row and returns a reference for that row without
       * rewriting the byte record or either earlier row."*
       *
       * Which is the fix for task `0132`: neither ingest is authoritative over
       * the other, because neither writes to something the other reads.
       */
      const key = rowKey(id, mime);
      const held = rows.get(key);
      if (held) return detach(held);
      const row: BlobRef = { id, mime, bytes: bytes.get(id)!.byteLength };
      rows.set(key, row);
      return detach(row);
    },
    async get(at) {
      const row = rowFor(at);
      if (!row) return undefined;
      const b = bytes.get(row.id);
      if (!b) return undefined;
      // Copied on the way out for the same reason it was copied on the way in:
      // a caller that writes into what it got back would silently change what
      // every other reference to that id resolves to.
      const out = new Uint8Array(b.byteLength);
      out.set(b);
      // The row's type, not the byte record's — a byte record has none, which
      // is the point of the split.
      return { bytes: out, mime: row.mime };
    },
    async stat(at) { const r = rowFor(at); return r ? detach(r) : undefined; },
    /**
     * `0047`: *"delete(id) retains its present administrative meaning: delete
     * the byte record and every typed metadata row for that digest."* It is
     * not a delete-one-type operation, and no caller may read it as reference
     * counting.
     */
    async delete(id) {
      bytes.delete(id);
      for (const [k, r] of rows) if (r.id === id) rows.delete(k);
    },
    /**
     * `0047`: *"list() lists accepted typed rows, so one digest may appear more
     * than once with different media types. A byte-usage total deduplicates
     * those rows by BlobId; a reference count does not."*
     */
    async list() { return [...rows.values()].map(detach); },
  };
}
